import { useLocale } from '@/renderer/utils/locale';
import { ArrowRightIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { useMemo } from 'react';
import { BettingTalkIcon, CBBIcon, CFBIcon, NFLIcon, RASPlusIcon } from './ProductIcons';
import calculatePrice from './calculatePrice';
import { sportIdFromName } from './productHelpers';

type MPO = API.MembershipProductOffer;

const BASE_STATS: API.MembershipProductStats = {
  canceled: 0,
  loss: 0,
  noAction: 0,
  pending: 0,
  played: 0,
  push: 0,
  units: 0,
  win: 0,
  buyable: 0,
  charged: 0,
  roi: 0,
  total: 0,
};

const BASE_OFFER: MPO = {
  id: 0,
  ids: [0],
  plays: 0,
  price: 0,
};

const OfferDetails = ({
  offer,
  product,
}: { offer: API.MembershipProductOffer; product: API.MembershipProduct }) => {
  const { locale } = useLocale();

  const discount = 0;

  const stats = useMemo(() => {
    if (product?.stats) {
      return {
        ...product.stats,
        played:
          product.stats.played ||
          (+product.stats.loss || 0) +
            (+product.stats.pending || 0) +
            (+product.stats.push || 0) +
            (+product.stats.win || 0) +
            (-product.stats.uncharged || 0),
      };
    }

    return undefined;
  }, [product?.stats]);

  const calcPrice = useMemo(() => {
    return calculatePrice({
      discount,
      stats: stats || BASE_STATS,
      variety: offer || BASE_OFFER,
    });
  }, [stats, offer]);

  const price = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(calcPrice.price);

  return (
    <div className="min-h-full w-full">
      <div className="flex h-full flex-col justify-between rounded bg-zinc-900/5 p-3 ring-1 ring-zinc-900/10 dark:bg-white/5 dark:ring-white/10">
        <div className="font-medium text-xs text-zinc-700 dark:text-zinc-300">
          {offer.ui?.title || `${offer.plays} plays`}
        </div>
        <div className="mt-4 font-medium text-2xl text-zinc-900 dark:text-white">{price}</div>
      </div>
    </div>
  );
};

const ProductListItem = ({
  product,
}: {
  product: API.MembershipProduct;
}) => {
  const IconComponent = useMemo(() => {
    switch (sportIdFromName(product?.sportIds?.[0])) {
      case 1:
        return CFBIcon;

      case 2:
        return CBBIcon;

      case 3:
        return NFLIcon;
    }

    if (product?.tags?.includes('ras-plus')) {
      return RASPlusIcon;
    }

    if (product?.tags?.includes('betting-talk')) {
      return BettingTalkIcon;
    }

    // @todo better default icon?
    return CFBIcon;
  }, [product?.sportIds, product?.tags]);

  const filteredOffers = [
    ...new Set(product?.offers.map((offer) => `${offer.price}-${offer.plays}`)),
  ].map((uniqueKey) => {
    const [price, plays] = uniqueKey.split('-');
    return product?.offers.find(
      (offer) => offer.price === parseFloat(price) && offer.plays === parseInt(plays, 10),
    );
  });

  return (
    <a className="group h-full cursor-pointer" href={`/order/${product?.id}`}>
      <div className="flex h-full cursor-pointer flex-col rounded-sm ring-1 ring-zinc-900/10 hover:bg-zinc-900/2.5 dark:bg-white/5 dark:ring-white/10 dark:hover:bg-white/10">
        <div className="flex grow flex-col justify-between border-zinc-900/10 border-b p-8">
          <div>
            <div className="flex flex-row items-center justify-start space-x-3">
              <IconComponent
                height={40}
                className="size-10 fill-emerald-900 text-emerald-900 dark:fill-white dark:text-white"
              />
              <h2 className="font-semibold text-2xl text-zinc-900 leading-tight tracking-tight dark:text-zinc-100">
                {product?.title}
              </h2>
            </div>
            <p className="mt-4 text-sm text-zinc-500 dark:text-zinc-300">{product?.description}</p>
            {product?.ui?.releaseTimes?.duration && (
              <div className="my-6 flex flex-row items-center space-x-4 text-zinc-500 sm:mb-10 dark:text-zinc-300">
                <CalendarDaysIcon className="inline-flex size-6 shrink-0" />
                <div className="font-bold text-sm">{product?.ui?.releaseTimes?.duration}</div>
              </div>
            )}
          </div>
          <div>
            <div className="mt-12 grid grid-cols-2 gap-4">
              {filteredOffers?.map((offer, index) => (
                <OfferDetails key={index} offer={offer} product={product} />
              ))}
            </div>
            <div className="mt-4 flex flex-row items-center justify-start space-x-1 text-zinc-700 dark:text-zinc-400">
              <span className="font-medium text-sm">More details</span>
              <ChevronRightIcon height={20} className="size-5 group-hover:hidden" />
              <ArrowRightIcon className="hidden size-5 group-hover:inline-block" />
            </div>
          </div>
        </div>
      </div>
    </a>
  );
};

const ProductList = ({
  products,
}: {
  products: API.MembershipProduct[];
}) => {
  return (
    <div className="mx-auto max-w-2xl px-4 py-8 lg:max-w-7xl lg:px-8 sm:px-6 sm:py-12">
      <div className="grid grid-cols-1 gap-y-4 lg:grid-cols-2 md:grid-cols-1 sm:grid-cols-2 lg:gap-x-8 sm:gap-x-6 sm:gap-y-10">
        {products &&
          products.map((product) => <ProductListItem key={product.id} product={product} />)}
      </div>
    </div>
  );
};

export default ProductList;
